// @flow
import React, { useState, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import { FormattedMessage } from 'react-intl';
import { Alert, Icon, Button } from 'antd';
import styles from './PDFViewer.styl';
import { setupPdfWorker, createPdfUrl, revokePdfUrl } from '../../utils/pdfUtils';

type Props = {
	file: Blob | string,
	fileName: string,
	onError?: (error: Error) => void,
};

const PDFViewer = ({ file, fileName, onError }: Props) => {
	const [numPages, setNumPages] = useState<number | null>(null);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [pdfError, setPdfError] = useState<string | null>(null);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);

	useEffect(() => {
		setupPdfWorker();
		setPageNumber(1);
		setNumPages(null);
		if (file instanceof Blob) {
			const url = createPdfUrl(file);
			setPdfUrl(url);
			return () => {
				revokePdfUrl(url);
			};
		}
		setPdfUrl(file);
		return undefined;
	}, [file]);

	function onDocumentLoadSuccess({ numPages: totalPages }: { numPages: number }) {
		setNumPages(totalPages);
		setPdfError(null);
	}

	function handleError(error: Error) {
		console.error('Error loading PDF:', error);
		setPdfError('Failed to load PDF. Please try again.');
		if (onError) {
			onError(error);
		}
	}

	function changePage(offset: number) {
		setPageNumber(prevPageNumber => {
			const newPage = prevPageNumber + offset;
			return Math.min(Math.max(newPage, 1), numPages || 1);
		});
	}

	function goToFirstPage() {
		setPageNumber(1);
	}

	function goToLastPage() {
		setNumPages((pages: number | null) => {
			if (pages !== null) {
				setPageNumber(pages);
			}
			return pages;
		});
	}

	function downloadPdf() {
		if (pdfUrl) {
			const link = document.createElement('a');
			link.href = pdfUrl;
			link.download = fileName;
			link.click();
		}
	}

	if (pdfError) {
		return <Alert message={pdfError} type="error" />;
	}

	const IconWrapper = ({ type, onClick, disabled }) => (
		<Icon
			type={type}
			onClick={disabled ? undefined : onClick}
			className={`${styles.controlIcon} ${disabled ? styles.disabled : ''}`}
		/>
	);

	return pdfUrl ? (
		<div className={styles.pdfViewer}>
			<div className={styles.topControls}>
				<Button type="primary" onClick={downloadPdf} className={styles.downloadButton}>
					<Icon type="download" />
					<FormattedMessage id="Proceq.Download" />
				</Button>
			</div>
			<Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} onLoadError={handleError}>
				<Page pageNumber={pageNumber} />
			</Document>
			<div className={styles.controls}>
				<div className={styles.navigationControls}>
					<IconWrapper type="double-left" onClick={goToFirstPage} disabled={pageNumber <= 1} />
					<IconWrapper type="left" onClick={() => changePage(-1)} disabled={pageNumber <= 1} />
					<p className={styles.pageInfo}>
						<FormattedMessage
							id="Proceq.PDFViewer.PageInfo"
							values={{ current: pageNumber, total: numPages }}
						/>
					</p>
					<IconWrapper type="right" onClick={() => changePage(1)} disabled={pageNumber >= (numPages || 1)} />
					<IconWrapper type="double-right" onClick={goToLastPage} disabled={pageNumber >= (numPages || 1)} />
				</div>
			</div>
		</div>
	) : null;
};

export default PDFViewer;
